<template>
  <public-layout>

    <!-- ═══════ BANNER ═══════ -->
    <div class="bc-banner">
      <div class="bc-banner__dots"></div>
      <div class="sc-container bc-banner__inner">
        <p class="bc-eyebrow">Blog</p>
        <h1 class="bc-banner__title">{{ category.name || 'Category' }}</h1>
        <p class="bc-banner__sub">Explore posts filed under this topic</p>
      </div>
    </div>

    <!-- ═══════ CONTENT ═══════ -->
    <section class="sc-container bc-main">

      <!-- Grid -->
      <div v-if="details.length" class="bc-grid">
        <article v-for="data in details" :key="data.id" class="bc-card">
          <router-link :to="'/blog/' + data.slug" class="bc-card__thumb-link">
            <img
              v-if="data.images && data.images.length"
              :src="data.images[0].path"
              class="bc-card__thumb"
              alt=""
            />
            <img v-else-if="data.image" :src="data.image" class="bc-card__thumb" alt="" />
            <div v-else class="bc-card__thumb bc-card__nophoto">
              <svg width="36" height="36" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
          </router-link>

          <div class="bc-card__body">
            <span v-if="data.category" class="bc-card__cat">{{ data.category.name }}</span>
            <router-link :to="'/blog/' + data.slug" class="bc-card__title">
              {{ data.title.length > 72 ? data.title.substring(0, 72) + '…' : data.title }}
            </router-link>
            <p class="bc-card__snippet" v-html="stripHtml(data.description).substring(0, 110) + '…'"></p>
            <div class="bc-card__foot">
              <span class="bc-card__date">{{ formatDate2(data.created_at) }}</span>
              <router-link :to="'/blog/' + data.slug" class="bc-read-link">
                Read
                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </router-link>
            </div>
          </div>
        </article>
      </div>

      <!-- Empty -->
      <div v-else-if="loaded" class="bc-empty">
        <svg width="52" height="52" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        <p>No posts in this category yet.</p>
        <router-link to="/blog" class="btn-ghost">Browse all posts</router-link>
      </div>

      <!-- Loading skeleton -->
      <div v-else class="bc-grid">
        <div v-for="n in 6" :key="n" class="bc-card bc-card--skeleton">
          <div class="bc-skel bc-skel--thumb"></div>
          <div class="bc-card__body">
            <div class="bc-skel bc-skel--line" style="width:40%;height:12px"></div>
            <div class="bc-skel bc-skel--line" style="width:90%;height:18px;margin-top:.5rem"></div>
            <div class="bc-skel bc-skel--line" style="width:75%;height:18px;margin-top:.25rem"></div>
            <div class="bc-skel bc-skel--line" style="width:60%;height:13px;margin-top:.75rem"></div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.lastPage > 1" class="bc-pagination">
        <button class="bc-pg-btn" :disabled="!pagination.prevPageUrl" @click="fetchData(pagination.prevPageUrl)">
          <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          Previous
        </button>
        <span class="bc-pg-info">Page {{ pagination.currentPage }} of {{ pagination.lastPage }}</span>
        <button class="bc-pg-btn" :disabled="!pagination.nextPageUrl" @click="fetchData(pagination.nextPageUrl)">
          Next
          <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>

    </section>

  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import axios from 'axios';
import moment from 'moment';
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'BlogCategory',
  components: { PublicLayout },

  created() {
    this.fetchData();
  },


  data() {
    return {
      loaded: false,
      details: [],
      category: {},
      pagination: {
        lastPage: 0,
        currentPage: 1,
        total: 0,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: '',
      },
    };
  },

  methods: {
    fetchData(url) {
      const endpoint = url || process.env.VUE_APP_API_URL + 'blog/category/' + this.$route.params.id;
      axios(endpoint)
        .then(response => {
          if (response.status !== 200) return;
          const data = response.data;
          this.details = data.data.data;
          this.category = data.cat;
          this.configPagination(data.data);
          useSEO({
            title: (this.category && this.category.name ? this.category.name + ' Articles' : 'Blog Category'),
            description: 'Browse ' + (this.category && this.category.name || 'category') + ' articles, news, and insights on the Structurecity blog.',
            url: SITE_ORIGIN + '/blog/category/' + (this.category && this.category.slug || this.$route.params.id),
          });
        })
        .catch(() => {})
        .finally(() => { this.loaded = true; });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },

    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },

    formatDate2(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },
};
</script>

<style scoped>
/* ── Banner ─────────────────────────────────────────────── */
.bc-banner {
  position: relative;
  margin-top: 68px;
  background: linear-gradient(135deg, #0d1f36 0%, #1e3a5f 55%, #1a4a7a 100%);
  padding: 4rem 1.5rem 3.5rem;
  overflow: hidden;
}
.bc-banner__dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 36px 36px;
}
.bc-banner::after {
  content: ''; position: absolute; bottom: -1px; left: 0; right: 0;
  height: 56px; background: var(--surface-2, #f8fafc);
  clip-path: ellipse(60% 100% at 50% 100%);
}
.bc-banner__inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; align-items: center;
  text-align: center; gap: .625rem;
}
.bc-eyebrow {
  font-size: .75rem; font-weight: 700; letter-spacing: .1em;
  text-transform: uppercase; color: var(--accent, #f59e0b); margin: 0;
}
.bc-banner__title {
  font-size: clamp(1.75rem, 3.5vw, 2.5rem); font-weight: 800;
  color: #fff; letter-spacing: -.03em; margin: 0; line-height: 1.1;
  text-transform: capitalize;
}
.bc-banner__sub {
  font-size: 1rem; color: rgba(255,255,255,.6); margin: 0;
}

/* ── Main ───────────────────────────────────────────────── */
.bc-main { padding: 2.5rem 0 4rem; }

/* ── Grid ───────────────────────────────────────────────── */
.bc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}
.bc-card {
  background: #fff;
  border-radius: var(--radius-lg, 1.25rem);
  border: 1px solid var(--border-light, #f1f5f9);
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  overflow: hidden; display: flex; flex-direction: column;
  transition: box-shadow .2s, transform .2s;
}
.bc-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,.09); transform: translateY(-2px); }

.bc-card__thumb-link { display: block; flex-shrink: 0; }
.bc-card__thumb { width: 100%; height: 200px; object-fit: cover; display: block; }
.bc-card__nophoto {
  height: 200px; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0f4f8, #e2eaf4); color: #94a3b8;
}

.bc-card__body { padding: 1.25rem; display: flex; flex-direction: column; flex: 1; }
.bc-card__cat {
  font-size: .7rem; font-weight: 700; letter-spacing: .05em;
  text-transform: uppercase; color: var(--primary, #1e3a5f); margin-bottom: .375rem;
}
.bc-card__title {
  font-size: .9375rem; font-weight: 700; color: var(--text, #111827);
  text-decoration: none; line-height: 1.45; display: block; margin-bottom: .5rem;
}
.bc-card__title:hover { color: var(--primary, #1e3a5f); }
.bc-card__snippet {
  font-size: .8125rem; color: var(--text-muted, #6b7280);
  line-height: 1.55; margin: 0 0 1rem; flex: 1;
}
.bc-card__foot {
  display: flex; align-items: center; justify-content: space-between;
  border-top: 1px solid var(--border-light, #f1f5f9); padding-top: .75rem; margin-top: auto;
}
.bc-card__date { font-size: .75rem; color: var(--text-muted, #6b7280); }
.bc-read-link {
  display: inline-flex; align-items: center; gap: .3rem;
  font-size: .8125rem; font-weight: 700; color: var(--primary, #1e3a5f);
  text-decoration: none; transition: gap .15s;
}
.bc-read-link:hover { gap: .5rem; }

/* ── Skeleton ───────────────────────────────────────────── */
.bc-skel {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: .375rem;
}
.bc-skel--thumb { height: 200px; border-radius: 0; }
@keyframes shimmer { to { background-position: -200% 0; } }

/* ── Empty ──────────────────────────────────────────────── */
.bc-empty {
  display: flex; flex-direction: column; align-items: center;
  gap: 1rem; padding: 5rem 1rem; text-align: center;
  color: var(--text-muted, #6b7280);
}
.bc-empty svg { opacity: .3; }
.bc-empty p { font-size: 1rem; margin: 0; }

/* ── Pagination ─────────────────────────────────────────── */
.bc-pagination {
  display: flex; align-items: center; justify-content: center;
  gap: 1rem; padding: 2.5rem 0 0;
}
.bc-pg-btn {
  display: inline-flex; align-items: center; gap: .375rem;
  background: #fff; border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius, .625rem); padding: .5rem 1.125rem;
  font-size: .875rem; font-weight: 600; color: var(--text, #111827);
  cursor: pointer; transition: background .15s, border-color .15s;
}
.bc-pg-btn:disabled { opacity: .4; cursor: not-allowed; }
.bc-pg-btn:not(:disabled):hover { background: var(--surface-2, #f8fafc); border-color: var(--primary, #1e3a5f); }
.bc-pg-info { font-size: .875rem; color: var(--text-muted, #6b7280); }

/* ── Buttons ────────────────────────────────────────────── */
.btn-ghost {
  display: inline-flex; align-items: center; gap: .375rem;
  background: none; color: var(--text-muted, #6b7280);
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: var(--radius-full, 9999px);
  padding: .625rem 1.375rem; font-size: .9375rem; font-weight: 600;
  cursor: pointer; text-decoration: none;
  transition: background .15s, color .15s;
}
.btn-ghost:hover { background: var(--surface-2, #f8fafc); color: var(--text, #111827); }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .bc-banner { padding: 3rem 1.25rem 2.5rem; }
  .bc-grid { grid-template-columns: 1fr; }
}
</style>
